import { connect } from 'react-redux';
import { difference, get, keyBy, sortBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

/** Actions */
import { closeGenericModal, openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

/** Commons */
import { Button, ListView } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';
import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import EmptyState from '@commons/EmptyState';
import mixpanelUtils, { ENUM_EVENT_SOURCES, ENUM_EVENTS } from '@commons/utils/mixpanel';
import utilsXLS from '@commons/utils/makeXLS';

import { getAssociateSupplierProductsModalConfig } from '@admin/inventories/utils/associateSupplierProductsModal/getAssociateSupplierProductsModalConfig.js';

/** Components */
import { SelectAvailabilitiesStepContent } from '@admin/suppliers/supplierProducts/details/supplierProductStores/components/SupplierProductMappings/utils/availabilitiesModal';
import DeepsightFiltersButton from '@admin/components/FilterButton';

/** Hooks */
import { ENUM_QUERY_PARAMS, withListViewQueryParamHookHOC } from '@hooks/useListViewQueryParams';

/** Selectors */
import { canCreateSupplierProductMapping } from '@selectors/actions/storeActions';
import { getAuthorizedActions } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';

/** Services */
import { storeSupplierProductMapping as sspmService } from '@services/storeSupplierProductMapping';
import { supplier as supplierService } from '@services/supplier';
import storageAreaService from '@services/storageArea';
import storeService from '@services/store';

/** Utils */
import { ACTIONS, getListActions, getRowActions } from './utils/actions';
import {
  getChoicesForAvailabilityDropdown,
  getChoicesForDlcLossDropdown,
  getChoicesForStockDropdown,
  getChoicesForTransferDropdown,
} from './utils/constants';
import { getListColumns } from './utils/columns';
import modalsUtils from './utils/modals';

const StoreSupplierProductMapping = (props) => {
  const {
    storeParams,
    listViewQueryParams,
    setListViewQueryParams,
    // Redux states
    client,
    currency,
    user,
    authorizedActions,
    showSuccessMessage,
    showErrorMessage,
    openGenericModal,
    refreshGenericModal,
    closeGenericModal,
  } = props;

  const { id: storeId, name: storeName } = storeParams;

  const userLanguageCode = get(user, 'lnkLanguageAccountrel.code', 'fr');

  /** LIST */
  const [isLoading, setIsLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [rowActions, setRowActions] = useState([]);
  const [columns] = useState(getListColumns());
  const [columnsFilterList, setColumnsFilterList] = useState([]);

  /** SSPM DATA */
  const [storeSupplierProductMappings, setStoreSupplierProductMappings] = useState([]);
  const [selectedSSPMs, setSelectedSSPMs] = useState([]);
  const [filteredSupplierProducts, setFilteredSupplierProducts] = useState([]);
  const [selectedAvailabilities, setSelectedAvailabilities] = useState({
    available: true,
    hasStock: true,
    hasDlc: true,
    hasTransfer: true,
  });
  const [displayEmptyState, setDisplayEmptyState] = useState(false);
  const [storageAreaKeyById, setStorageAreaKeyById] = useState([]);

  /** FILTERS */
  const [filters, setFilters] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(true);
  const [suppliers, setSuppliers] = useState([]);

  // Order availability
  const [choicesForAvailabilityDropdown] = useState(getChoicesForAvailabilityDropdown());
  const [selectedAvailabilityDropdownItems, setSelectedAvailabilityDropdownItems] = useState(
    getChoicesForAvailabilityDropdown(),
  );
  // Used in stock
  const [choicesForStockDropdown] = useState(getChoicesForStockDropdown());
  const [selectedStockDropdownItems, setSelectedStockDropdownItems] = useState(
    getChoicesForStockDropdown(),
  );
  // Losses
  const [choicesForDlcLossDropdown] = useState(getChoicesForDlcLossDropdown());
  const [selectedDlcLossDropdownItems, setSelectedDlcLossDropdownItems] = useState(
    getChoicesForDlcLossDropdown(),
  );
  // Transfer
  const [choicesForTransferDropdown] = useState(getChoicesForTransferDropdown());
  const [selectedTransferDropdownItems, setSelectedTransferDropdownItems] = useState(
    getChoicesForTransferDropdown(),
  );
  // Association modal
  const [availableSPForAssociation, setAvailableSPForAssociation] = useState([]);
  const [spToBeAdded, setSpToBeAdded] = useState([]);

  // Action SA modals states
  const [storageAreasToMap, setStorageAreasToMap] = useState([]);
  const [dissociating, setDissociating] = useState(false);

  // Current with modal action and his concerned SSPMs
  const [concernedSSPMsByAction, setConcernedSSPMsByAction] = useState({});

  /** USE EFFECTS */

  useEffect(() => {
    (async () => {
      const suppliersOfClient = await getSuppliers();
      await refreshData(suppliersOfClient);
    })();
  }, [storeId]);

  useEffect(() => {
    updateColumnsFilterList(storeSupplierProductMappings);

    if (!isLoading) {
      setDisplayEmptyState(!storeSupplierProductMappings.length);
    }
  }, [isLoading, storeSupplierProductMappings]);

  useEffect(() => {
    if (
      (!dissociating && !concernedSSPMsByAction[ACTIONS.ADD_STORAGE_AREAS]) ||
      (dissociating && !concernedSSPMsByAction[ACTIONS.REMOVE_STORAGE_AREAS])
    ) {
      return;
    }

    const params = getStorageAreasManagementModalParams(dissociating);

    refreshGenericModal(params);
  }, [storageAreasToMap, dissociating, concernedSSPMsByAction]);

  useEffect(() => {
    if (!applyFilters) {
      return;
    }

    const filteredSspms = storeSupplierProductMappings.filter(
      ({ available, hasStock, hasDlc, hasTransfer }) => {
        const orderAvailable = selectedAvailabilityDropdownItems.some(
          ({ itemValue }) => itemValue === available,
        );

        const stockAvailable = selectedStockDropdownItems.some(
          ({ itemValue }) => itemValue === hasStock,
        );

        const dlcLossAvailable = selectedDlcLossDropdownItems.some(
          ({ itemValue }) => itemValue === hasDlc,
        );

        const transferAvailable = selectedTransferDropdownItems.some(
          ({ itemValue }) => itemValue === hasTransfer,
        );

        return orderAvailable && stockAvailable && dlcLossAvailable && transferAvailable;
      },
    );

    if (!advancedFilters || !advancedFilters.length) {
      setFilteredSupplierProducts(filteredSspms);
      return;
    }

    const filteredSspmsWithAdvancedFilters = advancedFilters.reduce(
      (result, { doFilter, propertyKey, value }) => doFilter(result, propertyKey, value),
      filteredSspms,
    );

    setFilteredSupplierProducts(filteredSspmsWithAdvancedFilters);
  }, [storeSupplierProductMappings, applyFilters, advancedFilters]);

  useEffect(() => {
    setActions(
      getListActions({
        selectedSupplierProducts: selectedSSPMs,
        filteredSupplierProducts,
        suppliers,
        user,
        client,
        handleAssociate,
        handleDeletion,
        handleExport,
        openSspmManagementModal,
        openStorageAreasManagementModal,
        authorizedActions,
      }),
    );

    setRowActions(
      getRowActions({
        suppliers,
        user,
        client,
        handleDeletion,
        handleExport,
        openSspmManagementModal,
        openStorageAreasManagementModal,
        authorizedActions,
      }),
    );
  }, [filteredSupplierProducts, selectedSSPMs, availableSPForAssociation]);

  useEffect(() => {
    if (!concernedSSPMsByAction[ACTIONS.HANDLE_AVAILABILITIES]) {
      return;
    }

    const params = getSspmModalParams();

    refreshGenericModal(params);
  }, [selectedAvailabilities, concernedSSPMsByAction]);

  useEffect(() => {
    const params = getAssociateSupplierProductsModalConfig({
      hasLocalCatalogs: client.hasLocalCatalogs,
      setSpToBeAdded,
      availableSPForAssociation,
      handleAddAssociation,
      title: i18next.t('ADMIN.STORES.MAPPINGS_ASSOCIATION_MODAL_TITLE', { storeName }),
    });

    refreshGenericModal(params);
  }, [spToBeAdded, availableSPForAssociation]);

  /** METHODS */
  const getSuppliers = async () => {
    let suppliersOfClient = [];

    try {
      suppliersOfClient = await supplierService.getSuppliersOfClient();
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIERS.FETCH_FAILURE'));
    } finally {
      setSuppliers(suppliersOfClient);

      return suppliersOfClient;
    }
  };

  const refreshData = async (suppliersOfClient) => {
    setIsLoading(true);

    await getStoreSupplierProductMappings(suppliersOfClient);
    await getSPToAssociate();
  };

  const getStoreSupplierProductMappings = async (suppliersOfClient) => {
    if (!storeId || !suppliersOfClient.length) {
      return;
    }

    const suppliersKeyById = keyBy(suppliersOfClient, 'id');

    try {
      const startDate = moment().startOf('day').format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
      const sspmsOfStore = await sspmService.getStoreSupplierProductMappingsOfStore(
        storeId,
        startDate,
      );

      const formattedSspm = sspmsOfStore.map((sspm) => {
        const { lnkSupplierproductStoresupplierproductmappingrel } = sspm;
        const {
          id: supplierProductId,
          supplierId,
          name,
          category,
          subCategory,
          price,
        } = lnkSupplierproductStoresupplierproductmappingrel;

        return {
          ...sspm,
          supplierProductId,
          supplierId,
          supplierName: get(suppliersKeyById, [supplierId, 'name'], ''),
          name,
          price,
          category: category || i18next.t('GENERAL.NONE_VALUE'),
          subCategory: subCategory || i18next.t('GENERAL.NONE_VALUE'),
        };
      });

      // Get storage areas of store
      const sspmWithSADetails = await getStorageAreasOfStore(formattedSspm);

      setStoreSupplierProductMappings(sortBy(sspmWithSADetails, 'name'));
    } catch {
      setStoreSupplierProductMappings([]);
      showErrorMessage(i18next.t('GENERAL.FETCH_SUPPLIER_PRODUCTS_FAILURE'));
    } finally {
      setIsLoading(false);
    }
  };

  const getSPToAssociate = async () => {
    try {
      const supplierProducts = await storeService.getNotAssociatedSupplierProducts(storeId);

      setAvailableSPForAssociation(supplierProducts);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FETCH_ERROR'));
    }
  };

  const getStorageAreasOfStore = async (formattedSspm) => {
    try {
      const storageAreas = await storageAreaService.getStorageAreasByClientId({
        storeIds: [storeId],
      });

      setStorageAreaKeyById(keyBy(storageAreas, 'id'));

      const saIds = storageAreas.map(({ id }) => id);

      const associatedStorageAreaIdsGroupBySPIds = storageAreas.reduce((acc, storageArea) => {
        // For each storage area, storageArea.supplierProductIds contains already associated supplier product ids
        storageArea.supplierProductIds.forEach((supplierProductId) => {
          if (!acc[supplierProductId]) {
            acc[supplierProductId] = [];
          }

          acc[supplierProductId].push(storageArea.id);
        });

        return acc;
      }, {});

      const formattedSspmWithStorageAreas = formattedSspm.map((item) => {
        const associatedStorageAreaIds = get(
          associatedStorageAreaIdsGroupBySPIds,
          [item.supplierProductId],
          null,
        );

        if (associatedStorageAreaIds) {
          return {
            ...item,
            associatedStorageAreaIds: associatedStorageAreaIdsGroupBySPIds[item.supplierProductId],
            associatedStorageAreaIdsCount: associatedStorageAreaIds.length,
            notAssociatedStorageAreaIds: difference(saIds, associatedStorageAreaIds),
          };
        }

        return {
          ...item,
          associatedStorageAreaIds: [],
          associatedStorageAreaIdsCount: 0,
          notAssociatedStorageAreaIds: saIds,
        };
      });

      return formattedSspmWithStorageAreas;
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORAGE_AREAS.FETCHING_ERROR'));
    }
  };

  const updateColumnsFilterList = (storeSupplierProductMappings) => {
    const columnsList = columns.filter(({ filterType }) => !!filterType);

    const updateColumnsFilterList = columnsList.reduce((result, column) => {
      const updatedColumn = { ...column };

      const { propertyKey } = column;

      if (['category', 'subCategory', 'supplierName'].includes(propertyKey)) {
        const list = Object.values(
          storeSupplierProductMappings.reduce((result, sspm, key) => {
            if (sspm[propertyKey]) {
              result[sspm[propertyKey]] = {
                id: key,
                name: sspm[propertyKey],
                status: sspm[propertyKey],
                value: sspm[propertyKey],
                [propertyKey]: sspm[propertyKey],
              };
            }

            return result;
          }, {}),
        );

        updatedColumn.list = sortBy(list, 'name');
      }

      result.push(updatedColumn);

      return result;
    }, []);

    setColumnsFilterList(updateColumnsFilterList);
  };

  const openSspmManagementModal = (concernedSSPMs) => {
    setConcernedSSPMsByAction({
      [ACTIONS.HANDLE_AVAILABILITIES]: concernedSSPMs,
    });

    const params = getSspmModalParams();

    openGenericModal(params);
  };

  const getSspmModalParams = () => ({
    actions: [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        ...GENERIC_MODAL_SAVE_BUTTON(),
        handleClick: handleSspmUpdate,
        preventClosing: true,
      },
    ],
    title: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_TITLE_UPDATE_AVAILABILITIES'),
    icon: '/images/inpulse/pen-black-small.svg',
    type: 'action',
    handleCloseCleanUp: () => {
      setSelectedAvailabilities({
        available: true,
        hasStock: true,
        hasDlc: true,
        hasTransfer: true,
      });

      setConcernedSSPMsByAction({});
    },
    component: SelectAvailabilitiesStepContent,
    data: {
      selectedAvailabilities,
      setSelectedAvailabilities,
    },
    height: 'auto',
  });

  const handleSspmUpdate = async () => {
    const concernedSSPMs = concernedSSPMsByAction[ACTIONS.HANDLE_AVAILABILITIES] || [];

    setIsLoading(true);

    try {
      const sspmIds = concernedSSPMs.map(({ id }) => id);

      setSelectedAvailabilities({
        available: true,
        hasStock: true,
        hasDlc: true,
        hasTransfer: true,
      });
      closeGenericModal();

      await sspmService.patchStoreSupplierProductMappingByIds(sspmIds, selectedAvailabilities);

      await getStoreSupplierProductMappings(suppliers);

      showSuccessMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.AVAILABILITIES_SUCCESSFULLY_UPDATED'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_UPDATE_SSPM_ERROR'));
    }
  };

  const getAvailableStorageAreas = (isDissociation) => {
    const keyToUse = isDissociation ? 'associatedStorageAreaIds' : 'notAssociatedStorageAreaIds';

    const concernedSSPMs =
      concernedSSPMsByAction[
        isDissociation ? ACTIONS.REMOVE_STORAGE_AREAS : ACTIONS.ADD_STORAGE_AREAS
      ] || [];

    const storageAreaIds = concernedSSPMs.reduce(
      (acc, { [keyToUse]: storageAreaIds }) => [...acc, ...storageAreaIds],
      [],
    );

    const uniqueStorageAreaIds = new Set(storageAreaIds);

    return Array.from(uniqueStorageAreaIds).map((id) => storageAreaKeyById[id]);
  };

  const getStorageAreasManagementModalParams = (isDissociation) => {
    const availableStorageAreas = getAvailableStorageAreas(isDissociation);

    const params = modalsUtils.getStorageAreaToMapParamModalConfig({
      handleSave: isDissociation ? handleSADissociation : handleSAAssociation,
      storageAreasToMap,
      setStorageAreasToMap,
      availableStorageAreas,
      isDissociation,
      setConcernedSSPMsByAction,
    });

    return params;
  };

  const openStorageAreasManagementModal = ({ isDissociation = false, concernedSSPMs }) => {
    setDissociating(isDissociation);

    setConcernedSSPMsByAction({
      [isDissociation ? ACTIONS.REMOVE_STORAGE_AREAS : ACTIONS.ADD_STORAGE_AREAS]: concernedSSPMs,
    });

    const params = getStorageAreasManagementModalParams(isDissociation);

    openGenericModal(params);
  };

  const handleSAAssociation = async () => {
    const concernedSSPMs = concernedSSPMsByAction[ACTIONS.ADD_STORAGE_AREAS];

    setIsLoading(true);

    try {
      const storageAreaIds = storageAreasToMap.map(({ id }) => id);

      await storageAreaService.addStorageAreaMappings(
        storageAreaIds,
        concernedSSPMs.map(({ supplierProductId }) => supplierProductId),
      );

      mixpanelUtils.sendMetric(ENUM_EVENTS.UPDATE_STORAGE_AREA, {
        source: ENUM_EVENT_SOURCES.STORE_DETAILS,
        storageAreaIds,
      });

      setStorageAreasToMap([]);
      setConcernedSSPMsByAction({});

      showSuccessMessage(i18next.t('ADMIN.STORES.SSPM_ACTION_ADD_TO_SA_SUCCESS'));

      await getStoreSupplierProductMappings(suppliers);
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORES.SSPM_ACTION_ADD_TO_SA_ERROR'));
    }
  };

  const handleSADissociation = async () => {
    const concernedSSPMs = concernedSSPMsByAction[ACTIONS.REMOVE_STORAGE_AREAS];

    setIsLoading(true);

    try {
      const storageAreaIds = storageAreasToMap.map(({ id }) => id);

      await storageAreaService.deleteStorageAreaMappings(
        storageAreaIds,
        concernedSSPMs.map(({ supplierProductId }) => supplierProductId),
      );

      mixpanelUtils.sendMetric(ENUM_EVENTS.UPDATE_STORAGE_AREA, {
        source: ENUM_EVENT_SOURCES.STORE_DETAILS,
        storageAreaIds,
      });

      setStorageAreasToMap([]);
      setConcernedSSPMsByAction({});

      showSuccessMessage(i18next.t('ADMIN.STORES.SSPM_ACTION_REMOVE_FROM_SA_SUCCESS'));

      await getStoreSupplierProductMappings(suppliers);
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORES.SSPM_ACTION_REMOVE_FROM_SA_ERROR'));
    }
  };

  const handleAddAssociation = async () => {
    setIsLoading(true);

    try {
      const mappings = spToBeAdded.map((supplierProduct) => ({
        ...supplierProduct,
        storeId,
        supplierProductId: supplierProduct.id,
        hasStock: true,
        available: true,
        hasDlc: true,
        hasTransfer: true,
      }));

      await sspmService.createMultipleStoreSupplierProductMapping(mappings);

      showSuccessMessage(
        i18next.t('ADMIN.SUPPLIER_PRODUCTS.CREATE_STORE_MAPPING_SUCCESS', {
          count: mappings.length,
        }),
      );

      refreshData(suppliers);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.CREATE_STORE_MAPPING_ERROR'));
    }
  };

  const handleAssociate = () => {
    const params = getAssociateSupplierProductsModalConfig({
      hasLocalCatalogs: client.hasLocalCatalogs,
      availableSPForAssociation,
      handleAddAssociation,
      setSpToBeAdded,
      title: i18next.t('ADMIN.STORES.MAPPINGS_ASSOCIATION_MODAL_TITLE', { storeName }),
    });

    openGenericModal(params);
  };

  const handleDeletion = async (concernedSSPMs) => {
    setIsLoading(true);

    const sspmIds = concernedSSPMs.map(({ id }) => id);

    try {
      await sspmService.deleteMultipleStoreSupplierProductMapping(sspmIds);

      await refreshData(suppliers);
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORES.DELETE_ASSOCIATION_ERROR'));
    }
  };

  const handleExport = (dataToExport) => {
    const sheetName = `${i18next.t('ADMIN.STORES.LIST_SHEET_TITLE_ENTITIES')}_${storeName}`;

    const formattedExportColumns = columns.map((column) => {
      if (['available', 'hasStock', 'hasDlc', 'hasTransfer'].includes(column.propertyKey)) {
        return {
          ...column,
          transform: (item) => i18next.t(item ? 'GENERAL.YES' : 'GENERAL.NO'),
        };
      }

      return column;
    });

    const sheet = utilsXLS.generateDefaultSheet(
      sheetName,
      formattedExportColumns,
      dataToExport,
      currency,
    );

    utilsXLS.makeXLS(i18next.t('ADMIN.STORES.LIST_SHEET_TITLE_ENTITIES'), [sheet]);
  };

  const renderEmptyState = () => (
    <EmptyState
      label={i18next.t('ADMIN.STORES.SSPM_EMPTY_STATE_TITLE')}
      labelColor={ENUM_COLORS.IP_BLACK_1}
      labelFont={ENUM_FONTS.H2_INTER}
      renderActionButton={() =>
        canCreateSupplierProductMapping(authorizedActions) && (
          <Button
            handleClick={handleAssociate}
            icon={'/images/inpulse/add-white-small.svg'}
            label={i18next.t('GENERAL.ASSOCIATE')}
          />
        )
      }
      subtitle={i18next.t('ADMIN.STORES.SSPM_EMPTY_STATE_SUBTITLE')}
      subtitleMargin={'8px'}
    />
  );

  if (displayEmptyState) {
    return renderEmptyState();
  }

  return (
    <ListView
      actions={actions}
      columns={columns}
      data={filteredSupplierProducts}
      defaultCurrentPage={listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE]}
      defaultMaxPerPage={listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE]}
      defaultOrderBy={listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY]}
      defaultOrderType={listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE]}
      defaultSearchInput={listViewQueryParams[ENUM_QUERY_PARAMS.SEARCH]}
      handleCurrentPageChange={(input) =>
        setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](input)
      }
      handleMaxPerPageChange={(input) =>
        setListViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE](input)
      }
      handleOrderByChange={(input) => setListViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY](input)}
      handleOrderTypeChange={(input) => setListViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE](input)}
      handleSearchInputChange={(input) => setListViewQueryParams[ENUM_QUERY_PARAMS.SEARCH](input)}
      isLoading={isLoading}
      languageCode={userLanguageCode}
      padding={'108px 24px 0px 24px'}
      placeholderShape={i18next.t('GENERAL.SEARCH')}
      renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
      renderFilterButton={() => (
        <DeepsightFiltersButton
          advancedFilters={advancedFilters}
          applyFilters={applyFilters}
          columnsFilterList={columnsFilterList}
          customMultipleDropDowns={[
            {
              id: 'availablility-supplier-products',
              icon: '/images/inpulse/order-black.svg',
              list: choicesForAvailabilityDropdown,
              defaultSelectedItems: choicesForAvailabilityDropdown,
              selectedItems: selectedAvailabilityDropdownItems,
              setSelectedItems: setSelectedAvailabilityDropdownItems,
            },
            {
              id: 'stock-supplier-products',
              icon: '/images/inpulse/stock-black.svg',
              list: choicesForStockDropdown,
              defaultSelectedItems: choicesForStockDropdown,
              selectedItems: selectedStockDropdownItems,
              setSelectedItems: setSelectedStockDropdownItems,
            },
            {
              id: 'dlc-loss-supplier-products',
              icon: '/images/inpulse/loss-black.svg',
              list: choicesForDlcLossDropdown,
              defaultSelectedItems: choicesForDlcLossDropdown,
              selectedItems: selectedDlcLossDropdownItems,
              setSelectedItems: setSelectedDlcLossDropdownItems,
            },
            {
              id: 'transfer-supplier-products',
              icon: '/images/icon-dropdown-grey.svg',
              list: choicesForTransferDropdown,
              defaultSelectedItems: choicesForTransferDropdown,
              selectedItems: selectedTransferDropdownItems,
              setSelectedItems: setSelectedTransferDropdownItems,
            },
          ]}
          filters={filters}
          isLoading={isLoading}
          minWidth={120}
          readOnly={isLoading}
          setAdvancedFilters={setAdvancedFilters}
          setApplyFilters={setApplyFilters}
          setFilters={setFilters}
          textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
        />
      )}
      rowActions={rowActions}
      setSelectedItems={setSelectedSSPMs}
    />
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/admin/stores/:id/supplier-products',
  ),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withListViewQueryParamHookHOC(StoreSupplierProductMapping));
